import React, { useState, useEffect } from 'react';
import '../../styles/UserManagement.css';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface User {
    id: number;
    email: string;
    phone_number: string;
    first_name: string;
    last_name: string;
    profile_picture_url?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    roles: Role[];
    permissions: string[];
    roleNames: string;
}

interface Role {
    id: number;
    name: string;
    display_name: string;
    description: string;
    permissions: string[];
    userCount?: number;
}

interface UserStats {
    total_users: number;
    active_users: number;
    inactive_users: number;
    new_users_30_days: number;
    new_users_7_days: number;
}

interface RoleStats {
    name: string;
    display_name: string;
    user_count: number;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

interface ConfirmationModal {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
    onCancel: () => void;
    isDestructive?: boolean;
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [userStats, setUserStats] = useState<UserStats | null>(null);
    const [roleStats, setRoleStats] = useState<RoleStats[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const [confirmModal, setConfirmModal] = useState<ConfirmationModal>({
        isOpen: false,
        title: '',
        message: '',
        confirmText: '',
        onConfirm: () => { },
        onCancel: () => { },
        isDestructive: false
    });

    // Fetch data functions
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                ...(searchTerm && { search: searchTerm }),
                ...(statusFilter && { status: statusFilter }),
                ...(roleFilter && { role: roleFilter })
            });

            const response = await fetch(`${BACKEND_URL}/api/admin/users?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data.users);
                setPagination(data.pagination);
            } else {
                throw new Error('Failed to fetch users');
            }
        } catch (err) {
            setError('Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/roles`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRoles(data.roles);
            } else {
                throw new Error('Failed to fetch roles');
            }
        } catch (err) {
            console.error('Error fetching roles:', err);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/users/stats`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUserStats(data.userStats);
                setRoleStats(data.roleStats);
            } else {
                throw new Error('Failed to fetch stats');
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchUserDetails = async (userId: number) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSelectedUser({ ...data.user, stats: data.stats });
            } else {
                throw new Error('Failed to fetch user details');
            }
        } catch (err) {
            setError('Failed to load user details');
            console.error('Error fetching user details:', err);
        }
    };

    // Action functions
    const toggleUserStatus = async (user: User) => {
        const newStatus = !user.is_active;
        const action = newStatus ? 'activate' : 'deactivate';

        setConfirmModal({
            isOpen: true,
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
            message: `Are you sure you want to ${action} ${user.first_name}? ${!newStatus ? 'This will disable their access to the application.' : 'This will restore their access.'
                }`,
            confirmText: action.charAt(0).toUpperCase() + action.slice(1),
            isDestructive: !newStatus,
            onConfirm: async () => {
                try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/users/${user.id}/status`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({
                            active: newStatus,
                            reason: `${action}d by admin`
                        })
                    });

                    if (response.ok) {
                        setSuccess(`User ${action}d successfully`);
                        fetchUsers();
                        fetchStats();
                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.error || `Failed to ${action} user`);
                    }
                } catch (err) {
                    setError(`Failed to ${action} user: ${(err as Error).message}`);
                }

                setConfirmModal({ ...confirmModal, isOpen: false });
            },
            onCancel: () => {
                setConfirmModal({ ...confirmModal, isOpen: false });
            }
        });
    };

    const assignRole = async (userId: number, roleId: string) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/roles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ roleId: parseInt(roleId) })
            });

            if (response.ok) {
                setSuccess('Role assigned successfully');
                fetchUsers();
                fetchStats();
                if (selectedUser?.id === userId) {
                    fetchUserDetails(userId);
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to assign role');
            }
        } catch (err) {
            setError(`Failed to assign role: ${(err as Error).message}`);
        }
    };

    const removeRole = async (userId: number, roleId: number, roleName: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Remove Role',
            message: `Are you sure you want to remove the "${roleName}" role from this user?`,
            confirmText: 'Remove',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/roles/${roleId}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });

                    if (response.ok) {
                        setSuccess('Role removed successfully');
                        fetchUsers();
                        fetchStats();
                        if (selectedUser?.id === userId) {
                            fetchUserDetails(userId);
                        }
                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Failed to remove role');
                    }
                } catch (err) {
                    setError(`Failed to remove role: ${(err as Error).message}`);
                }

                setConfirmModal({ ...confirmModal, isOpen: false });
            },
            onCancel: () => {
                setConfirmModal({ ...confirmModal, isOpen: false });
            }
        });
    };

    const resetPassword = async (user: User) => {
        setConfirmModal({
            isOpen: true,
            title: 'Reset Password',
            message: `Reset password for ${user.first_name}? A new temporary password will be sent to their email.`,
            confirmText: 'Reset Password',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/users/${user.id}/reset-password`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({ sendEmail: true })
                    });

                    if (response.ok) {
                        setSuccess('Password reset successfully. User will receive email with new password.');
                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Failed to reset password');
                    }
                } catch (err) {
                    setError(`Failed to reset password: ${(err as Error).message}`);
                }

                setConfirmModal({ ...confirmModal, isOpen: false });
            },
            onCancel: () => {
                setConfirmModal({ ...confirmModal, isOpen: false });
            }
        });
    };

    // Effects
    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchStats();
    }, [pagination.page, pagination.limit, searchTerm, statusFilter, roleFilter]);

    // Debounced search
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (pagination.page !== 1) {
                setPagination({ ...pagination, page: 1 });
            } else {
                fetchUsers();
            }
        }, 300);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (isActive: boolean) => (
        <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
        </span>
    );

    return (
        <div className="user-management">
            <div className="um-header">
                <h2>User Management</h2>
                <p>Manage users, roles, and account permissions</p>
            </div>

            {/* Statistics Cards */}
            {userStats && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>{userStats.total_users}</h3>
                        <p>Total Users</p>
                    </div>
                    <div className="stat-card">
                        <h3>{userStats.active_users}</h3>
                        <p>Active Users</p>
                    </div>
                    <div className="stat-card">
                        <h3>{userStats.inactive_users}</h3>
                        <p>Inactive Users</p>
                    </div>
                    <div className="stat-card">
                        <h3>{userStats.new_users_7_days}</h3>
                        <p>New This Week</p>
                    </div>
                </div>
            )}

            {/* Role Statistics */}
            {roleStats.length > 0 && (
                <div className="role-stats">
                    <h3>Role Distribution</h3>
                    <div className="role-badges">
                        {roleStats.map((role) => (
                            <div key={role.name} className="role-stat">
                                <span className="role-name">{role.display_name}</span>
                                <span className="role-count">{role.user_count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filter-group">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>

                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Roles</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.name}>
                                {role.display_name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Error/Success Messages */}
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Users Table */}
            <div className="table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Contact</th>
                            <th>Roles</th>
                            <th>Status</th>
                            <th>Joined</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="loading-cell">
                                    <div className="loading-spinner"></div>
                                    Loading users...
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="empty-cell">
                                    No users found matching your criteria
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => (
                                <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-avatar">
                                                {user.profile_picture_url ? (
                                                    <img src={user.profile_picture_url} alt={user.first_name} />
                                                ) : (
                                                    <div className="avatar-placeholder">
                                                        {(user.first_name?.[0] ).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="user-details">
                                                <div className="user-name">
                                                    {user.first_name || user.last_name
                                                        ? `${user.first_name} ${user.last_name}`.trim()
                                                        : 'user'}
                                                </div>
                                                <div className="user-name">{[user.first_name, user.last_name].filter(Boolean).join(' ')}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="contact-info">
                                            <div>{user.email}</div>
                                            <div className="phone">{user.phone_number}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-roles">
                                            {user.roleNames || 'No roles'}
                                        </div>
                                    </td>
                                    <td>{getStatusBadge(user.is_active)}</td>
                                    <td>{formatDate(user.created_at)}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => {
                                                    fetchUserDetails(user.id);
                                                    setShowUserModal(true);
                                                }}
                                                className="btn btn-sm btn-outline"
                                                title="View Details"
                                            >
                                                👁️
                                            </button>
                                            <button
                                                onClick={() => toggleUserStatus(user)}
                                                className={`btn btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                                                title={user.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                {user.is_active ? '🔒' : '🔓'}
                                            </button>
                                            <button
                                                onClick={() => resetPassword(user)}
                                                className="btn btn-sm btn-danger"
                                                title="Reset Password"
                                            >
                                                🔑
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        disabled={pagination.page === 1}
                        className="btn btn-outline"
                    >
                        Previous
                    </button>
                    <span className="page-info">
                        Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                    </span>
                    <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        disabled={pagination.page === pagination.pages}
                        className="btn btn-outline"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* User Detail Modal */}
            {showUserModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>User Details</h3>
                            <button onClick={() => setShowUserModal(false)} className="modal-close">✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="user-profile">
                                <div className="profile-section">
                                    <h4>Profile Information</h4>
                                    <div className="profile-grid">
                                        <div><strong>Email:</strong> {selectedUser.email}</div>
                                        <div><strong>Phone:</strong> {selectedUser.phone_number}</div>
                                        <div><strong>Full Name:</strong> {`${selectedUser.first_name} ${selectedUser.last_name}`.trim()}</div>
                                        <div><strong>Status:</strong> {getStatusBadge(selectedUser.is_active)}</div>
                                        <div><strong>Joined:</strong> {formatDate(selectedUser.created_at)}</div>
                                    </div>
                                </div>

                                <div className="roles-section">
                                    <div className="section-header">
                                        <h4>Roles & Permissions</h4>
                                        <button
                                            onClick={() => setShowRoleModal(true)}
                                            className="btn btn-sm btn-primary"
                                        >
                                            Assign Role
                                        </button>
                                    </div>
                                    <div className="current-roles">
                                        {selectedUser.roles?.length > 0 ? (
                                            selectedUser.roles.map((role) => (
                                                <div key={role.id} className="role-item">
                                                    <span className="role-badge">{role.display_name}</span>
                                                    <button
                                                        onClick={() => removeRole(selectedUser.id, role.id, role.display_name)}
                                                        className="btn btn-xs btn-danger"
                                                        title="Remove role"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <p>No roles assigned</p>
                                        )}
                                    </div>
                                </div>

                                {(selectedUser as any).stats && (
                                    <div className="stats-section">
                                        <h4>Activity Statistics</h4>
                                        <div className="stats-grid">
                                            <div className="stat-item">
                                                <span className="stat-number">{(selectedUser as any).stats.routesCreated}</span>
                                                <span className="stat-label">Routes Created</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-number">{(selectedUser as any).stats.tripsAsDriver}</span>
                                                <span className="stat-label">Trips as Driver</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-number">{(selectedUser as any).stats.tripsAsPassenger}</span>
                                                <span className="stat-label">Trips as Passenger</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Role Assignment Modal */}
            {showRoleModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Assign Role</h3>
                            <button onClick={() => setShowRoleModal(false)} className="modal-close">✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="role-assignment">
                                <p>Select a role to assign to <strong>{selectedUser.first_name}</strong>:</p>
                                <select
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    className="role-select"
                                >
                                    <option value="">Select a role...</option>
                                    {roles
                                        .filter(role => !selectedUser.roles?.some(ur => ur.id === role.id))
                                        .map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.display_name} - {role.description}
                                            </option>
                                        ))}
                                </select>
                                <div className="modal-actions">
                                    <button
                                        onClick={() => {
                                            if (newRole) {
                                                assignRole(selectedUser.id, newRole);
                                                setShowRoleModal(false);
                                                setNewRole('');
                                            }
                                        }}
                                        disabled={!newRole}
                                        className="btn btn-primary"
                                    >
                                        Assign Role
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowRoleModal(false);
                                            setNewRole('');
                                        }}
                                        className="btn btn-outline"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmModal.isOpen && (
                <div className="modal-overlay">
                    <div className="modal confirmation-modal">
                        <div className="modal-header">
                            <h3>{confirmModal.title}</h3>
                        </div>
                        <div className="modal-body">
                            <p>{confirmModal.message}</p>
                            <div className="modal-actions">
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className={`btn ${confirmModal.isDestructive ? 'btn-danger' : 'btn-primary'}`}
                                >
                                    {confirmModal.confirmText}
                                </button>
                                <button onClick={confirmModal.onCancel} className="btn btn-outline">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;